import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import GanttTareasService from '../axios_services/ganttTareas.service';
import Loader from '../components/Loader/Loader';
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import ModalTareaGantt from '../components/Modal/ModalTareaGantt';
import ModalImagenesGantt from '../components/Modal/ModalImagenesGantt';
import { FileMinus } from 'lucide-react';
import './TareasGantt.css';

const PERFILES_ADMIN = [1, 8, 9, 26];

function TareasGantt() {
    const [state] = useContext(AppContext);
    const [tareas, setTareas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTarea, setSelectedTarea] = useState(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [tareaImagenes, setTareaImagenes] = useState(null);
    const [vistaActiva, setVistaActiva] = useState('tabla');

    const userProfile = state.catalogos?.perfiles?.find(
        p => String(p.perfil_id) === String(state.user?.perfil_id)
    );
    const perfilId = parseInt(state.user?.perfil_id);
    const esAdmin = PERFILES_ADMIN.includes(perfilId);
    const areaId = parseInt(userProfile?.area_id) || parseInt(state.user?.area_id) || null;

    const [areasCatalog] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 29 && [1, 2, 3, 4, 5, 7, 8].some(v => v == t.tipo_id)));
    const [prioridadCatalog] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 42));
    const [estadosCatalog] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 41));
    const [areaFiltro, setAreaFiltro] = useState(() => {
        const primero = state.catalogos.tipos.find(t => t.categoria_id == 29 && [1, 2, 3, 4, 5, 7, 8].some(v => v == t.tipo_id));
        return primero ? String(primero.tipo_id) : '';
    });

    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [responsableFiltro, setResponsableFiltro] = useState('');
    const areaIdModal = areaId || areaFiltro || null;

    const usuariosArea = useMemo(() => {
        const areaParaFiltrar = esAdmin ? (areaFiltro || null) : areaId;
        if (!areaParaFiltrar) return state.user?.personal || [];
        const personal = state.user?.personal || [];
        if (String(areaParaFiltrar) === '1') {
            const perfilesArea1 = ['6', '3', '23'];
            return personal.filter(u => perfilesArea1.includes(String(u.perfil_id)));
        }
        const perfiles = state.catalogos?.perfiles || [];
        const perfilesDelArea = perfiles
            .filter(p => String(p.area_id) === String(areaParaFiltrar))
            .map(p => String(p.perfil_id));
        return personal.filter(u => perfilesDelArea.includes(String(u.perfil_id)));
    }, [areaFiltro, areaId, esAdmin, state.catalogos, state.user]);

    useEffect(() => {
        setResponsableFiltro('');
    }, [areaFiltro, areaId]);

    useEffect(() => {
        cargarTareas();
    }, [areaFiltro, estadoFiltro, responsableFiltro, periodo]);

    const cargarTareas = async () => {
        setIsLoading(true);
        const areaParam = esAdmin ? (areaFiltro || null) : areaId;
        try {
            const response = await GanttTareasService.getTareasGantt(areaParam, periodo, estadoFiltro, responsableFiltro);
            setTareas(response.data || []);
        } catch (error) {
            console.error(error);
            setTareas([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        await cargarTareas();
    };

    const handleNuevaTarea = () => {
        setSelectedTarea(null);
        setIsModalOpen(true);
    };

    const handleEditarTarea = (tarea) => {
        setSelectedTarea(tarea);
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        setSelectedTarea(null);
        if (shouldReload === true) {
            cargarTareas();
        }
    };

    const handleSaveTarea = async (tareaData) => {
        setIsLoading(true);
        try {
            let response;
            if (tareaData.id) {
                response = await GanttTareasService.updateTareaGantt(tareaData);
            } else {
                response = await GanttTareasService.insertTareaGantt(tareaData);
            }
            return response.data || response;
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleEliminarTarea = async (tarea) => {
        if (tarea.estado === 3) {
            alert('No se puede eliminar una tarea en estado "Completado".');
            return;
        }
        if (!window.confirm('¿Está seguro de eliminar esta tarea?')) return;
        setIsLoading(true);
        try {
            await GanttTareasService.deleteTareaGantt({ id: tarea.id });
            await cargarTareas();
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al eliminar.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    const getGanttBarStyle = (tarea) => {
        const [year, month] = periodo.split('-').map(Number);
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0);
        const totalDays = monthEnd.getDate();

        const tareaInicio = new Date(tarea.fecha_inico);
        const tareaFin = new Date(tarea.fecha_fin);

        const effectiveStart = tareaInicio < monthStart ? monthStart : tareaInicio;
        const effectiveEnd = tareaFin > monthEnd ? monthEnd : tareaFin;

        if (effectiveStart > monthEnd || effectiveEnd < monthStart) {
            return null;
        }

        const leftPct = ((effectiveStart.getDate() - 1) / totalDays) * 100;
        const widthPct = ((effectiveEnd.getDate() - effectiveStart.getDate() + 1) / totalDays) * 100;

        return { left: `${leftPct}%`, width: `${Math.max(widthPct, 1)}%` };
    };

    const getDaysInMonth = () => {
        const [year, month] = periodo.split('-').map(Number);
        return new Date(year, month, 0).getDate();
    };

    const isWeekendDay = (day) => {
        const [year, month] = periodo.split('-').map(Number);
        const dow = new Date(year, month - 1, day).getDay();
        return dow === 0;
    };

    const getGanttSegments = (tarea) => {
        const [year, month] = periodo.split('-').map(Number);
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0);
        const totalDays = monthEnd.getDate();

        const tareaInicio = new Date(tarea.fecha_inico);
        const tareaFin = new Date(tarea.fecha_fin);

        const effectiveStart = tareaInicio < monthStart ? monthStart : tareaInicio;
        const effectiveEnd = tareaFin > monthEnd ? monthEnd : tareaFin;

        if (effectiveStart > monthEnd || effectiveEnd < monthStart) return null;

        const startDay = effectiveStart.getDate();
        const endDay = effectiveEnd.getDate();

        const segments = [];
        let segStart = null;

        for (let d = startDay; d <= endDay; d++) {
            const dow = new Date(year, month - 1, d).getDay();
            const isWE = dow === 0;
            if (!isWE) {
                if (segStart === null) segStart = d;
            } else {
                if (segStart !== null) {
                    segments.push({
                        left: `${((segStart - 1) / totalDays) * 100}%`,
                        width: `${((d - 1 - segStart + 1) / totalDays) * 100}%`,
                    });
                    segStart = null;
                }
            }
        }
        if (segStart !== null) {
            segments.push({
                left: `${((segStart - 1) / totalDays) * 100}%`,
                width: `${((endDay - segStart + 1) / totalDays) * 100}%`,
            });
        }
        return segments.length > 0 ? segments : null;
    };

    if (isLoading) return <Loader />;

    const totalDias = getDaysInMonth();
    const diasArray = Array.from({ length: totalDias }, (_, i) => i + 1);

    return (
        <div className="caja-flujo-container tareas-gantt-scope">
            <h1>GESTIÓN DE ACTIVIDADES</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>
                {esAdmin && (
                    <div className="campo-fecha">
                        <label>Área:</label>
                        <select
                            value={areaFiltro}
                            onChange={(e) => setAreaFiltro(e.target.value)}
                            className="form-control"
                        >
                            {areasCatalog.map(a => (
                                <option key={a.tipo_id} value={a.tipo_id}>{a.descripcion}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select
                        value={estadoFiltro}
                        onChange={(e) => setEstadoFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos los estados</option>
                        {estadosCatalog.map(e => (
                            <option key={e.tipo_id} value={e.tipo_id}>{e.descripcion}</option>
                        ))}
                    </select>
                </div>
                <div className="campo-fecha">
                    <label>Responsable:</label>
                    <select
                        value={responsableFiltro}
                        onChange={(e) => setResponsableFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos</option>
                        {usuariosArea.map(u => (
                            <option key={u.usuario_id} value={u.nombre_completo_usuario}>{u.nombre_completo_usuario}</option>
                        ))}
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={handleSearch} isLoading={isLoading} />

            <div className="nuevo-movimiento-section" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '15px' }}>
                <div className="gantt-view-toggle">
                    <button
                        className={`gantt-toggle-btn ${vistaActiva === 'tabla' ? 'active' : ''}`}
                        onClick={() => setVistaActiva('tabla')}
                    >
                        Tabla
                    </button>
                    <button
                        className={`gantt-toggle-btn ${vistaActiva === 'gantt' ? 'active' : ''}`}
                        onClick={() => setVistaActiva('gantt')}
                    >
                        Gantt
                    </button>
                </div>
                <ButtonInsert onClick={handleNuevaTarea} />
            </div>

            {
                vistaActiva === 'tabla' && (
                    <div className="table-container">
                        <Table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Actividades</th>
                                    <th>Responsable</th>
                                    <th>Fecha Inicio</th>
                                    <th>Fecha Fin</th>
                                    <th>Prioridad</th>
                                    <th>Estado</th>
                                    <th>Evidencia</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tareas.length > 0 ? (
                                    tareas.map((tarea, idx) => {
                                        const prioridadDesc = prioridadCatalog.find(p => p.tipo_id == tarea.prioridad)?.descripcion || tarea.prioridad;
                                        const estadoDesc = estadosCatalog.find(e => e.tipo_id == tarea.estado)?.descripcion || tarea.estado;
                                        return (
                                            <tr key={tarea.id}>
                                                <td>{idx + 1}</td>
                                                <td>{tarea.nombre?.toUpperCase()}</td>
                                                <td>{tarea.responsable ? (() => { const p = tarea.responsable.trim().split(' '); return `${p[0]} ${p.length >= 4 ? p[2] : p[1] || ''}`.trim().toUpperCase(); })() : '-'}</td>
                                                <td>{formatFecha(tarea.fecha_inico)}</td>
                                                <td>{formatFecha(tarea.fecha_fin)}</td>
                                                <td>
                                                    <span className={`gantt-prioridad-badge prioridad-${prioridadDesc?.toLowerCase()}`}>
                                                        {prioridadDesc}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`gantt-estado-badge estado-${tarea.estado}`}>
                                                        {estadoDesc}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                                        <button
                                                            onClick={() => { setTareaImagenes(tarea); setIsImageModalOpen(true); }}
                                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                                                            title={tarea.tiene_imagenes ? 'Ver evidencia' : 'Adjuntar evidencia'}
                                                        >
                                                            <FileMinus size={20} style={{ color: tarea.tiene_imagenes ? '#3cb167' : '#f87171', opacity: tarea.tiene_imagenes ? 1 : 0.7 }} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                        <ButtonUpdate onClick={() => handleEditarTarea(tarea)} />
                                                        <ButtonDelete onClick={() => handleEliminarTarea(tarea)} />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={9} className="text-center text-gray-500 py-4">
                                            No hay tareas registradas para este periodo.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )
            }

            {
                vistaActiva === 'gantt' && (
                    <div className="gantt-chart-container">
                        <div className="gantt-chart">
                            <div className="gantt-header-row">
                                <div className="gantt-label-col">Actividad</div>
                                <div className="gantt-timeline-col">
                                    {diasArray.map(d => (
                                        <div key={d} className={`gantt-day-cell${isWeekendDay(d) ? ' gantt-day-weekend' : ''}`}>{d}</div>
                                    ))}
                                </div>
                            </div>
                            <div className="gantt-header-row gantt-header-dow">
                                <div className="gantt-label-col"></div>
                                <div className="gantt-timeline-col">
                                    {diasArray.map(d => {
                                        const [year, month] = periodo.split('-').map(Number);
                                        const dow = new Date(year, month - 1, d).getDay();
                                        const iniciales = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
                                        return (
                                            <div key={d} className={`gantt-day-cell gantt-day-dow${isWeekendDay(d) ? ' gantt-day-weekend' : ''}`}>
                                                {iniciales[dow]}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {
                                tareas.length > 0 ? (
                                    tareas.map((tarea) => {
                                        const segments = getGanttSegments(tarea);
                                        const estadoLabel = estadosCatalog.find(e => e.tipo_id == tarea.estado)?.descripcion || '';
                                        return (
                                            <div key={tarea.id} className="gantt-row">
                                                <div className="gantt-label-col" title={tarea.nombre}>
                                                    <span className="gantt-task-name">{tarea.nombre?.toUpperCase()}</span>
                                                </div>
                                                <div className="gantt-timeline-col gantt-bar-container" style={{ '--days': totalDias }}>
                                                    {diasArray.map(d => isWeekendDay(d) && (
                                                        <div
                                                            key={d}
                                                            className="gantt-weekend-overlay"
                                                            style={{
                                                                left: `${((d - 1) / totalDias) * 100}%`,
                                                                width: `${(1 / totalDias) * 100}%`,
                                                            }}
                                                        />
                                                    ))}
                                                    {segments ? (
                                                        segments.map((seg, i) => (
                                                            <div
                                                                key={i}
                                                                className={`gantt-bar estado-${tarea.estado}`}
                                                                style={seg}
                                                                title={`${tarea.nombre} | ${estadoLabel}`}
                                                            >
                                                                {i === 0 && <span className="gantt-bar-label">{estadoLabel}</span>}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="gantt-fuera-rango">Fuera del mes</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="gantt-empty">No hay tareas para mostrar en este periodo.</div>
                                )
                            }
                        </div>

                        <div className="gantt-leyenda">
                            {estadosCatalog.map(e => (
                                <div key={e.tipo_id} className="gantt-leyenda-item">
                                    <span className={`gantt-leyenda-dot estado-${e.tipo_id}`}></span>
                                    <span>{e.descripcion}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            <ModalTareaGantt
                isOpen={isModalOpen}
                tarea={selectedTarea}
                onSave={handleSaveTarea}
                onClose={handleCloseModal}
                areaId={areaIdModal}
                areasCatalog={areasCatalog}
                prioridadCatalog={prioridadCatalog}
                estadosCatalog={estadosCatalog}
                usuarios={usuariosArea}
                currentUserNombre={
                    state.user?.personal?.find(
                        p => String(p.usuario_id) === String(state.user?.usuario_id)
                    )?.nombre_completo_usuario
                    || state.user?.nombres
                    || ''
                }
            />

            <ModalImagenesGantt
                isOpen={isImageModalOpen}
                onClose={() => { setIsImageModalOpen(false); setTareaImagenes(null); cargarTareas(); }}
                tarea={tareaImagenes}
                usuarioId={state.user?.usuario_id}
            />
        </div>
    );

}

export default TareasGantt;
